// === SAA:UTILS:STEP-COMPLETENESS ===
// "Does step X still pass its own Volgende gate on the current data?" One predicate per
// progress step, reusing the screens' validators. The stepper offers a forward click only
// while every step before the target is complete: unchanged data = free travel, changed
// products / blanked fields = walk the flow again. Product-details is deliberately ALL
// selected products priced (stricter than the summary's any-product mount guard) so a
// product added after the offer can never be skipped.
import { hasAnySelectedProduct } from './productGuards';
import { hasCalculatedPremium } from './productPremium';
import { getProductOrder } from '../workflowConfig';
import { isClientInfoComplete } from '../pages/screens/client-information/validation';
import { validateIBAN, validateStartDate } from '../pages/screens/payment/validation';

export interface WorkflowReads {
  selectedProducts: Record<string, boolean | undefined> | null | undefined;
  getStepData: (stepId: string) => any;
  paymentData?: any;
}

export const productsComplete = ({ selectedProducts }: WorkflowReads): boolean =>
  hasAnySelectedProduct(selectedProducts as any);

export const clientInformationComplete = ({ getStepData }: WorkflowReads): boolean =>
  isClientInfoComplete(getStepData('client-information')?.clientInfo);

export const productDetailsComplete = ({ selectedProducts, getStepData }: WorkflowReads): boolean => {
  const selected = getProductOrder().filter((id) => selectedProducts?.[id]);
  return selected.length > 0 && selected.every((id) => hasCalculatedPremium(id, getStepData(id)));
};

export const paymentComplete = ({ getStepData, paymentData }: WorkflowReads): boolean => {
  const paymentInfo = getStepData('payment-information')?.paymentInfo || paymentData?.paymentInfo;
  const requestData = getStepData('payment-information-request');
  return !!(
    paymentInfo?.iban &&
    validateIBAN(paymentInfo.iban) &&
    validateStartDate(paymentInfo.startDate) === null &&
    requestData &&
    Object.keys(requestData).length > 0
  );
};

export const acceptanceComplete = ({ getStepData }: WorkflowReads): boolean =>
  !!getStepData('acceptance-questions')?.formData;

/** Progress step id -> its completeness gate (ids = config workflow.progressSteps / applicationProgressSteps). */
export const STEP_COMPLETE: Record<string, (reads: WorkflowReads) => boolean> = {
  products: productsComplete,
  'client-information': clientInformationComplete,
  'product-details': productDetailsComplete,
  payment: paymentComplete,
  questions: acceptanceComplete,
};
